import { useState, useCallback, useEffect, useRef } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { EAVTable } from "./EAVTable";
import { GenerateButton } from "./GenerateButton";
import { TabHelperBox } from "./TabHelperBox";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Info, Loader2, Database, Trash2 } from "lucide-react";
import type {
  ThematicMap,
  ThematicEntity,
  ThematicEntityAttribute,
  ThematicNodeWithEntity,
  NewThematicEntityAttribute,
  PromptStep,
  LLMEAVOutput,
} from "@/types/thematicMaps";

interface EAVSectionProps {
  map: ThematicMap;
  entities: ThematicEntity[];
  attributes: ThematicEntityAttribute[];
  attributesLoading: boolean;
  nodes: ThematicNodeWithEntity[];
  generating: boolean;
  currentStep: PromptStep | null;
  runEAVExtraction: (
    map: ThematicMap,
    entities: ThematicEntity[],
    nodes: ThematicNodeWithEntity[],
    onProgress?: (msg: string) => void,
  ) => Promise<LLMEAVOutput[] | null>;
  onCreateAttribute: (
    data: Omit<NewThematicEntityAttribute, "user_id" | "map_id">,
  ) => Promise<ThematicEntityAttribute | null>;
  onCreateAttributesBulk: (
    data: Omit<NewThematicEntityAttribute, "user_id" | "map_id">[],
  ) => Promise<ThematicEntityAttribute[]>;
  onDeleteAttribute: (id: string) => Promise<boolean>;
  onDeleteAllAttributes: () => Promise<boolean>;
}

const HELPER_ITEMS = [
  '<strong>Entity</strong> — encja (np. "ryz do sushi"), ktorej atrybuty opisujesz.',
  '<strong>Attribute</strong> — nazwa wlasciwosci (np. "kleistosc", "czas_przygotowania").',
  '<strong>Value</strong> — wartosc wlasciwosci (np. "wysoka", "15 minut").',
  "<strong>Typ</strong> — text, number, boolean lub enum.",
  "<strong>Pewnosc</strong> — 0.0-1.0: pewnosc AI co do poprawnosci (reczne = 1.0).",
  "<strong>Cel EAV</strong> — wzbogaca graf wiedzy o szczegolowe cechy encji, poprawiajac jakosc tresci.",
];

export function EAVSection({
  map,
  entities,
  attributes,
  attributesLoading,
  nodes,
  generating,
  currentStep,
  runEAVExtraction,
  onCreateAttribute,
  onCreateAttributesBulk,
  onDeleteAttribute,
  onDeleteAllAttributes,
}: EAVSectionProps) {
  const { toast } = useToast();
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Elapsed timer
  useEffect(() => {
    if (statusMessage) {
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      setElapsed(0);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [statusMessage]);

  const hasEntities = entities.length > 0;

  // Unique entity count in attributes
  const entityIdsWithAttrs = new Set(attributes.map((a) => a.entity_id));

  // EAV extraction handler
  const handleExtractEAV = useCallback(async () => {
    setStatusMessage("Przygotowywanie ekstrakcji atrybutow EAV...");
    try {
      const result = await runEAVExtraction(
        map,
        entities,
        nodes,
        setStatusMessage,
      );
      if (!result) return;

      setStatusMessage(
        `Wyekstrahowano atrybuty dla ${result.length} encji. Rozwiazywanie...`,
      );

      // Build entity name → id mapping
      const entityNameToId = new Map<string, string>();
      for (const entity of entities) {
        entityNameToId.set(entity.canonical_name.toLowerCase(), entity.id);
        for (const alias of entity.aliases || []) {
          if (!entityNameToId.has(alias.toLowerCase())) {
            entityNameToId.set(alias.toLowerCase(), entity.id);
          }
        }
      }

      // Build existing attribute keys for dedup
      const existingKeys = new Set(
        attributes.map(
          (a) =>
            `${a.entity_id}|${a.attribute_name.toLowerCase()}|${a.value.toLowerCase()}`,
        ),
      );

      const resolved: Omit<NewThematicEntityAttribute, "user_id" | "map_id">[] =
        [];
      let skippedEntity = 0;
      let skippedDupe = 0;

      for (const entityResult of result) {
        const entityId = entityNameToId.get(
          entityResult.entity_canonical_name.toLowerCase(),
        );
        if (!entityId) {
          skippedEntity++;
          continue;
        }

        for (const attr of entityResult.attributes) {
          const key = `${entityId}|${attr.attribute_name.toLowerCase()}|${attr.value.toLowerCase()}`;
          if (existingKeys.has(key)) {
            skippedDupe++;
            continue;
          }

          resolved.push({
            entity_id: entityId,
            attribute_name: attr.attribute_name.toLowerCase(),
            value: attr.value,
            value_type: attr.value_type,
            source: "ai",
            confidence: Math.max(0, Math.min(1, attr.confidence)),
            notes: "",
            sort_order: 0,
          });
          existingKeys.add(key);
        }
      }

      const totalSkipped = skippedEntity + skippedDupe;

      if (resolved.length === 0) {
        const reasons = [];
        if (skippedEntity > 0)
          reasons.push(`${skippedEntity} nieznanych encji`);
        if (skippedDupe > 0) reasons.push(`${skippedDupe} duplikatow`);
        toast({
          title: "Brak nowych atrybutow",
          description:
            reasons.length > 0
              ? `Pominieto ${totalSkipped}: ${reasons.join(", ")}`
              : "Wszystkie atrybuty juz istnieja",
          variant: "destructive",
        });
        return;
      }

      setStatusMessage(`Zapisywanie ${resolved.length} atrybutow do bazy...`);
      await onCreateAttributesBulk(resolved);

      toast({
        title: "Sukces",
        description:
          `Zapisano ${resolved.length} atrybutow` +
          (totalSkipped > 0 ? ` (pominieto ${totalSkipped})` : ""),
      });
    } finally {
      setStatusMessage(null);
    }
  }, [
    map,
    entities,
    nodes,
    attributes,
    runEAVExtraction,
    onCreateAttributesBulk,
    toast,
  ]);

  return (
    <div className="space-y-4">
      <TabHelperBox title="Entity-Attribute-Value (EAV)" items={HELPER_ITEMS} />

      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">
          Atrybuty ({attributes.length})
        </h3>
        <div className="flex items-center gap-2">
          {attributes.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => {
                if (
                  confirm(
                    `Usunac WSZYSTKIE ${attributes.length} atrybutow? Tej operacji nie mozna cofnac.`,
                  )
                ) {
                  onDeleteAllAttributes();
                }
              }}
              disabled={generating}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Usun wszystkie
            </Button>
          )}
          <GenerateButton
            onClick={handleExtractEAV}
            generating={generating && currentStep === "eav_extraction"}
            label="Wyciagnij EAV z AI"
            loadingLabel="Ekstrakcja EAV..."
            disabled={!hasEntities}
          />
        </div>
      </div>

      {/* Progress */}
      {statusMessage && (
        <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950 px-4 py-3 text-sm text-blue-700 dark:text-blue-300">
          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
          <span className="flex-1">{statusMessage}</span>
          {elapsed > 0 && (
            <span className="text-xs text-blue-500 tabular-nums">
              {elapsed}s
            </span>
          )}
        </div>
      )}

      {/* Gating */}
      {!hasEntities && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Najpierw wygeneruj Encje — atrybuty EAV opisuja istniejace encje.
          </AlertDescription>
        </Alert>
      )}

      {/* Stats */}
      {hasEntities && attributes.length > 0 && (
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Database className="h-3.5 w-3.5" />
          <span>
            {attributes.length} atrybutow dla {entityIdsWithAttrs.size}/
            {entities.length} encji
          </span>
        </div>
      )}

      <EAVTable
        attributes={attributes}
        entities={entities}
        loading={attributesLoading}
        onCreateAttribute={onCreateAttribute}
        onDeleteAttribute={onDeleteAttribute}
      />
    </div>
  );
}
